import {useLayoutEffect} from 'react';
import {Helmet} from 'react-helmet-async';
import {useLocation} from 'react-router-dom';
import {domains} from '../data/lessons';

const SITE_URL='https://medicalcodingclassesonline.com';

const tools={
  '/em-calculator': {
    title: 'Free E/M Coding Calculator | Medical Coding Tool',
    description: 'Interactive Evaluation and Management (E/M) code selection tool based on official CPT coding guidelines.',
  },
  '/term-dictionary': {
    title: 'Medical Terminology Dictionary & Search | Study Prep',
    description: 'Searchable medical terminology dictionary covering root words, prefixes, suffixes, and clinical definitions.',
  },
  '/modifier-tool': {
    title: 'CPT & HCPCS Modifier Lookup Tool | Medical Coding Class',
    description: 'Quick lookup tool for CPT and HCPCS Level II modifiers with clinical coding examples and use cases.',
  },
  '/icd10-quiz': {
    title: 'ICD-10-CM Coding Practice Quiz | Free Self-Assessment',
    description: 'Practice ICD-10-CM diagnostic coding with interactive quiz questions, conventions, and immediate explanations.',
  },
};

const home={
  title: 'Medical Coding Classes Online | Top Cert Prep & Guides',
  description: 'Ace your certification exam with our medical coding classes online, interactive study tools, flashcards, & practice quizzes. Start studying today!',
  socialTitle: 'Medical Coding Classes Online',
  socialDescription: 'Online classes for medical billing and coding.',
};

function decodePathSegment(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function normalizePathname(pathname='') {
  const rawPath=String(pathname).split(/[?#]/)[0] || '/';
  const segments=rawPath
    .split('/')
    .filter(Boolean)
    .map(decodePathSegment);

  return segments.length ? `/${segments.join('/')}` : '/';
}

function buildCanonicalUrl(path='') {
  const baseUrl=SITE_URL.replace(/\/+$/,'');
  const cleanPath=normalizePathname(path);

  return cleanPath==='/' ? `${baseUrl}/` : `${baseUrl}${cleanPath}`;
}

function toReadableSlug(value='') {
  return decodePathSegment(value)
    .replace(/[-_]+/g,' ')
    .replace(/\s+/g,' ')
    .trim()
    .replace(/\b\w/g,(letter)=> letter.toUpperCase());
}

function createFallbackMetadata(pathname) {
  const segments=normalizePathname(pathname).split('/').filter(Boolean);
  const slug=segments[segments.length - 1] || 'Medical Coding Classes Online';
  const readableSlug=toReadableSlug(slug);
  const title=`${readableSlug} | Medical Coding Classes Online`;
  const description=`Study ${readableSlug.toLowerCase()} with free medical billing and coding lessons, practice tools, and exam preparation resources.`;

  return {
    title,
    description,
    socialTitle: title,
    socialDescription: description,
  };
}

function getLessonMetadata(domainId,lessonId,pathname) {
  const domain=domains.find((item)=> item.id===domainId);
  const lesson=domain?.lessons.find((item)=> item.id===lessonId);

  if (!lesson) return createFallbackMetadata(pathname);

  const description=lesson.summary || lesson.description || lesson.overview;
  const title=`${lesson.title} | Medical Coding Class Online`;

  return {
    title,
    description,
    socialTitle: title,
    socialDescription: description,
  };
}

function getRouteMetadata(pathname) {
  const normalizedPath=normalizePathname(pathname);
  const toolPath=normalizedPath.toLowerCase();
  const tool=tools[toolPath];

  if (tool) {
    return {
      metadata: {
        ...tool,
        socialTitle: tool.title,
        socialDescription: tool.description,
      },
      canonicalPath: toolPath,
    };
  }

  const segments=normalizedPath.split('/').filter(Boolean);
  const routeType=segments[0]?.toLowerCase();
  const routeId=segments[1];
  const routeChildId=segments[2];

  if (routeType==='lesson' && routeId && routeChildId) {
    return {
      metadata: getLessonMetadata(routeId,routeChildId,normalizedPath),
      canonicalPath: `/lesson/${routeId}/${routeChildId}`,
    };
  }

  if ((routeType==='module' || routeType==='modules') && routeId) {
    const module=domains.find((item)=> item.id===routeId);

    if (module) {
      const title=`${module.title} | Medical Coding Classes Online`;

      return {
        metadata: {
          title,
          description: module.description,
          socialTitle: title,
          socialDescription: module.description,
        },
        canonicalPath: `/${routeType}/${routeId}`,
      };
    }

    return {
      metadata: createFallbackMetadata(normalizedPath),
      canonicalPath: `/${routeType}/${routeId}`,
    };
  }

  if (routeType==='lesson' || routeType==='module' || routeType==='modules') {
    return {
      metadata: createFallbackMetadata(normalizedPath),
      canonicalPath: normalizedPath,
    };
  }

  if (normalizedPath==='/') {
    return {
      metadata: home,
      canonicalPath: '/',
    };
  }

  return {
    metadata: createFallbackMetadata(normalizedPath),
    canonicalPath: normalizedPath,
  };
}

function RouteMetadata() {
  const {pathname}=useLocation();
  const {metadata,canonicalPath}=getRouteMetadata(pathname);
  const canonicalUrl=buildCanonicalUrl(canonicalPath);

  useLayoutEffect(()=> {
    document.title=metadata.title;
    window.prerenderReady=false;

    const frame=window.requestAnimationFrame(()=> {
      window.prerenderReady=true;
    });

    return ()=> {
      window.cancelAnimationFrame(frame);
      window.prerenderReady=false;
    };
  },[metadata.title]);

  return (
    <Helmet>
      <title>{metadata.title}</title>
      <meta name="description" content={metadata.description} />
      <meta property="og:title" content={metadata.socialTitle} />
      <meta property="og:description" content={metadata.socialDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <link rel="canonical" href={canonicalUrl} />
    </Helmet>
  );
}

export default RouteMetadata;